import React, { createContext, useState, useContext } from 'react';
import { firestore, timestamp } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

// Création du contexte des coupons
const CouponContext = createContext();

// Hook personnalisé pour utiliser le contexte des coupons
export const useCoupon = () => {
  return useContext(CouponContext);
};

// Fournisseur du contexte des coupons
export const CouponProvider = ({ children }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour récupérer tous les coupons
  const getCoupons = async () => {
    try {
      setLoading(true);
      const couponsCollection = collection(firestore, 'coupons');
      const couponsSnapshot = await getDocs(couponsCollection);
      const couponsList = couponsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCoupons(couponsList);
      setLoading(false);
      return couponsList;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Fonction pour récupérer un coupon par ID
  const getCouponById = async (id) => {
    try {
      const couponDoc = await getDoc(doc(firestore, 'coupons', id));
      if (couponDoc.exists()) {
        return { id: couponDoc.id, ...couponDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour récupérer un coupon par code
  const getCouponByCode = async (code) => {
    try {
      const couponsCollection = collection(firestore, 'coupons');
      const q = query(couponsCollection, where("code", "==", code));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const couponDoc = querySnapshot.docs[0];
        return { id: couponDoc.id, ...couponDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour créer un nouveau coupon
  const createCoupon = async (couponData) => {
    try {
      const newCoupon = {
        ...couponData,
        status: 'active',
        createdAt: timestamp(),
        updatedAt: timestamp()
      };
      const docRef = await addDoc(collection(firestore, 'coupons'), newCoupon);
      return { id: docRef.id, ...newCoupon };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour mettre à jour un coupon
  const updateCoupon = async (id, couponData) => {
    try {
      const couponRef = doc(firestore, 'coupons', id);
      const updatedData = {
        ...couponData,
        updatedAt: timestamp()
      };
      await updateDoc(couponRef, updatedData);
      return { id, ...updatedData };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour désactiver un coupon
  const deactivateCoupon = async (id) => {
    try {
      const couponRef = doc(firestore, 'coupons', id);
      await updateDoc(couponRef, {
        status: 'inactive',
        deactivatedAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'inactive' };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Valeur du contexte
  const value = {
    coupons,
    loading,
    error,
    getCoupons,
    getCouponById,
    getCouponByCode,
    createCoupon,
    updateCoupon,
    deactivateCoupon
  };

  return (
    <CouponContext.Provider value={value}>
      {children}
    </CouponContext.Provider>
  );
};

export { CouponContext };
